import { action, computed } from 'mobx'
import {
  type AuthStore,
  type AuthInitialScreen,
} from '@/features/iam/auth/AuthStore'
import { task } from 'mobx-task'
import { type SessionStore } from '@/features/iam/auth/SessionStore'
import { type WorkspaceMembershipStore } from '@/features/iam/workspaces/WorkspaceMembershipStore'
import { type WorkspaceStore } from '@/features/iam/workspaces/WorkspaceStore'
import { type MemberStore } from '@/features/iam/members/MemberStore'
import { Id } from '@/infra/id'
import { WorkspaceSelectorViewModel } from '@/features/iam/workspaces/WorkspaceSelector/WorkspaceSelectorViewModel'
import { WorkspaceSignupViewModel } from '@/features/iam/workspaces/WorkspaceSignup/WorkspaceSignupViewModel'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { NotFound } from 'fejl'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import { type RedirectLoginOptions } from '@auth0/auth0-spa-js'
import { type ParsedQs } from 'qs'
import {
  DefaultLastWorkspaceStorage,
  type LastWorkspaceStorage,
} from '@/features/iam/workspaces/LastWorkspaceStorage'
import type { Storage } from '@/infra/storage'

/**
 * Which part of the authentication screen is showing. Values double as the
 * screen's CSS class names.
 */
export type AuthenticationDisplay =
  'authentication' | 'configuration' | 'portal' | 'portalClosed'

export class AuthenticationViewModel {
  readonly workspaceSelectorVm: WorkspaceSelectorViewModel
  readonly workspaceSignupVm: WorkspaceSignupViewModel
  private readonly _lastWorkspaceStorage: LastWorkspaceStorage
  private readonly _jobManagementLastWorkspaceStorage: LastWorkspaceStorage

  constructor(
    private readonly _sessionStore: SessionStore,
    private readonly _workspaceMembershipsStore: WorkspaceMembershipStore,
    private readonly _workspaceStore: WorkspaceStore,
    private readonly _memberStore: MemberStore,
    private readonly _authStore: AuthStore,
    private readonly _navigate: NavigateFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _localStorage: Storage,
    private readonly _sessionStorage: Storage,
  ) {
    this._lastWorkspaceStorage = new DefaultLastWorkspaceStorage(_localStorage)
    this._jobManagementLastWorkspaceStorage = new DefaultLastWorkspaceStorage(
      _localStorage,
      'jobManagement',
    )
    this.workspaceSelectorVm = new WorkspaceSelectorViewModel(
      this._workspaceMembershipsStore,
      this._workspaceStore,
      this._sessionStore,
      this._checkMemberExists,
      this._lastWorkspaceStorage,
      this._localStorage,
      this._sessionStorage,
    )

    this.workspaceSignupVm = new WorkspaceSignupViewModel(
      this._workspaceStore,
      this._sessionStore,
      this._memberStore,
      this._workspaceMembershipsStore,
      this._createAlertErrorHandler,
      this._checkMemberExists,
    )
  }

  handleParams = task(async (qs: ParsedQs) => {
    if (this.isAuthenticated) {
      return
    }

    if (
      typeof qs.error_description === 'string' &&
      qs.error_description === 'UNABLE_TO_LINK'
    ) {
      this._newAlert()
        .withState('error')
        .saying(
          'We were unable to link your accounts. Please reach out to support for help.',
        )
        .autoDismiss()
        .show()
    }

    if (
      typeof qs.error_description === 'string' &&
      qs.error_description === 'UNABLE_TO_SIGN_IN'
    ) {
      this._newAlert()
        .withState('error')
        .saying('Something went wrong. Please reach out to support for help.')
        .autoDismiss()
        .show()
    }

    if (typeof qs.show === 'string' && ['login', 'signUp'].includes(qs.show)) {
      const opts: RedirectLoginOptions = {}

      if (typeof qs.connection === 'string') {
        opts.authorizationParams = {
          ...opts.authorizationParams,
          connection: qs.connection,
        }
      }

      if (typeof qs.login_hint === 'string') {
        opts.authorizationParams = {
          ...opts.authorizationParams,
          login_hint: qs.login_hint,
        }
      }

      await this.authorize(qs.show as AuthInitialScreen, opts)
    }
  })

  /**
   * Loads the session, accepting the invitation when there is a token.
   */
  fetch = task(async (inviteToken?: string) => {
    await this._checkAuthentication()

    if (!this.isAuthenticated) {
      return
    }

    if (inviteToken) {
      this._navigate(links.welcome(), { replace: true })
    }

    const { invitedWorkspace, memberships } =
      await this._sessionStore.fetchMemberships(inviteToken)
    if (!invitedWorkspace && memberships.length === 0) {
      this.workspaceSignupVm.configurer.show()
    }

    await this.workspaceSelectorVm.preSelectWorkspace(
      invitedWorkspace ?? undefined,
    )
    await this.workspaceSignupVm.prefillAndSync()
  })

  /**
   * Checks to ensure we are authenticated.
   */
  private async _checkAuthentication() {
    await this._authStore
      .checkAuthentication()
      // `fromError` would split Auth0 messages containing ':' into a title and description.
      .catch((err: Error) =>
        this._newAlert().withState('error').saying(err.message).show(),
      )
  }

  @computed
  get isAuthenticated() {
    return this._sessionStore.isAuthenticated
  }

  @computed
  get hasWorkspaceSelected() {
    return this._sessionStore.hasWorkspaceSelected
  }

  /**
   * Which part of the authentication screen is showing.
   */
  @computed
  get display(): AuthenticationDisplay {
    if (!this.isAuthenticated) {
      return 'authentication'
    }

    if (this.workspaceSignupVm.configurer.showing) {
      return 'configuration'
    }

    return this.workspaceSelectorVm.selector.showing ? 'portal' : 'portalClosed'
  }

  @action.bound
  login() {
    return this.authorize('login')
  }

  @action.bound
  signUp() {
    return this.authorize('signUp')
  }

  @action.bound
  authorize(initialScreen: AuthInitialScreen, opts?: RedirectLoginOptions) {
    return this._authStore.redirectToAuthorize(initialScreen, opts)
  }

  private readonly _checkMemberExists = async (workspace: Workspace) => {
    const member = await this._memberStore
      .fetchMember({
        workspaceId: workspace.id,
        userId: this._sessionStore.currentUser.id,
        userPublicId: this._sessionStore.currentUser.publicId,
      })
      .catch(NotFound.ignore(null))
    if (
      // If the member doesn't exist within the workspace
      !member?.internalId ||
      // .. or the member exists but we dont have names and emails
      member.givenName.length === 0 ||
      member.familyName.length === 0 ||
      member.email.length === 0
    ) {
      void this.workspaceSignupVm.maybeSyncMember(workspace)
      return false
    }

    this._setLastSelectedWorkspace(
      Id.assert(workspace.id),
      Id.assert(this._sessionStore.currentUser.publicId),
    )

    return true
  }

  private _setLastSelectedWorkspace(workspaceId: string, userId: string) {
    this._lastWorkspaceStorage.save(workspaceId, userId)

    // Job Management supports selecting multiple workspaces, which a single
    // workspace must not overwrite. User Management keeps its own selection,
    // so it isn't updated here.
    const existingJobManagement =
      this._jobManagementLastWorkspaceStorage.load(userId)
    if (existingJobManagement) {
      try {
        const existingIds = JSON.parse(existingJobManagement) as unknown
        if (
          Array.isArray(existingIds) &&
          existingIds.length > 1 &&
          existingIds.every((id) => typeof id === 'string')
        ) {
          return
        }
      } catch {
        // An unreadable selection is overwritten below.
      }
    }

    this._jobManagementLastWorkspaceStorage.save(
      JSON.stringify([workspaceId]),
      userId,
    )
  }
}
